import React from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import translations from '../contexts/translations';

const Cancellation = ({ language = 'en' }) => {
  const t = translations[language];

  const cancellationPolicies = [
    {
      service: t.hotelReservations,
      icon: <CheckCircle className="w-6 h-6 text-green-600" />,
      freeCancel: t.hotelFreeCancel,
      partialRefund: t.hotelPartialRefund,
      noRefund: t.hotelNoRefund
    },
    {
      service: t.circuitTours,
      icon: <CheckCircle className="w-6 h-6 text-green-600" />,
      freeCancel: t.toursFreeCancel,
      partialRefund: t.toursPartialRefund,
      noRefund: t.toursNoRefund
    },
    {
      service: t.carRentals,
      icon: <CheckCircle className="w-6 h-6 text-green-600" />,
      freeCancel: t.carFreeCancel,
      partialRefund: t.carPartialRefund,
      noRefund: t.carNoRefund
    },
    {
      service: t.guidedTours,
      icon: <CheckCircle className="w-6 h-6 text-green-600" />,
      freeCancel: t.guidedFreeCancel,
      partialRefund: t.guidedPartialRefund,
      noRefund: t.guidedNoRefund
    }
  ];

  return (
    <div className="py-24 min-h-screen relative overflow-hidden" style={{background: 'linear-gradient(to bottom, #fff7ed, #f9e9d2 80%)'}}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,186,73,0.08),transparent_60%)]"></div>
      <div className="container mx-auto px-6 max-w-7xl relative z-10" style={{ marginTop: '3rem' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-6 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
            {t.cancellationPolicy || 'Politique d\'annulation'}
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-amber-900">{t.cancellationPageTitle}</h1>
          <p className="text-lg max-w-3xl mx-auto leading-relaxed text-amber-900/80">
            {t.cancellationPageSubtitle}
          </p>
        </motion.div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white/90 border border-amber-100 rounded-3xl p-8 mb-12 shadow-xl"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-xl mr-4 bg-orange-100">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-orange-600">{t.importantNotice}</h3>
              <p className="text-amber-900/80 mt-1">
                {t.importantNoticeDesc}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Cancellation Policies by Service */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20"
        >
          {cancellationPolicies.map((policy, index) => (
            <div key={index} className="bg-white/90 border border-amber-100 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="p-3 rounded-xl mr-4 bg-orange-100">
                  {policy.icon}
                </div>
                <h3 className="text-xl font-semibold text-orange-600">{policy.service}</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span className="text-amber-900/80">
                    <strong className="text-amber-900">{t.freeCancellation}:</strong> {policy.freeCancel}
                  </span>
                </div>
                <div className="flex items-start">
                  <Clock className="w-5 h-5 text-yellow-500 mr-3 mt-0.5" />
                  <span className="text-amber-900/80">
                    <strong className="text-amber-900">{t.partialRefund}:</strong> {policy.partialRefund}
                  </span>
                </div>
                <div className="flex items-start">
                  <XCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5" />
                  <span className="text-amber-900/80">
                    <strong className="text-amber-900">{t.noRefund}:</strong> {policy.noRefund}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* How to Cancel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white/90 border border-amber-100 rounded-3xl p-8 mb-12 shadow-xl"
        >
          <h2 className="text-2xl font-semibold mb-8 text-orange-600 text-center">{t.howToCancel}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-2xl font-bold text-orange-600">1</span>
              </div>
              <h3 className="font-semibold mb-3 text-amber-900">{t.step1Title}</h3>
              <p className="text-amber-900/80">{t.step1Desc}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-2xl font-bold text-orange-600">2</span>
              </div>
              <h3 className="font-semibold mb-3 text-amber-900">{t.step2Title}</h3>
              <p className="text-amber-900/80">{t.step2Desc}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-2xl font-bold text-orange-600">3</span>
              </div>
              <h3 className="font-semibold mb-3 text-amber-900">{t.step3Title}</h3>
              <p className="text-amber-900/80">{t.step3Desc}</p>
            </div>
          </div>
        </motion.div>

        {/* Contact for Help */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="text-center"
        >
          <div className="rounded-3xl p-12 bg-gradient-to-r from-orange-50 via-amber-100 to-orange-50 border border-amber-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 rounded-full transform translate-x-24 -translate-y-24 blur-3xl bg-orange-100/40"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full transform -translate-x-32 translate-y-32 blur-3xl bg-orange-100/30"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4 text-orange-600">{t.needHelpTitle}</h2>
              <p className="max-w-2xl mx-auto mb-6 text-lg text-amber-900/80">
                {t.needHelpDesc}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="inline-flex items-center px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl bg-orange-600 text-white hover:bg-orange-700">
                  {t.contactSupport}
                </button>
                <button className="inline-flex items-center px-8 py-3 rounded-xl font-semibold transition-all duration-300 border border-orange-200 text-orange-600 hover:bg-orange-50">
                  {t.liveChat}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Cancellation;